/**
 * Normalizes hex color to 6-digit format without #.
 * Supports 3, 4, 6, and 8 digit formats (alpha is stripped).
 */
function normalizeHexColor(color: string): string | null {
  const hex = color.replace('#', '').trim().toLowerCase();

  if (hex.length === 6 && /^[0-9a-f]+$/.test(hex)) {
    return hex;
  }

  if (hex.length === 8 && /^[0-9a-f]+$/.test(hex)) {
    return hex.slice(0, 6);
  }

  if (hex.length === 3 && /^[0-9a-f]+$/.test(hex)) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length === 4 && /^[0-9a-f]+$/.test(hex)) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  return null;
}

/** Extracts first hex color from a CSS gradient string. */
function extractFirstHexFromGradient(gradient: string): string | null {
  const hexPattern = /#([0-9a-fA-F]{3,8})\b/g;
  const matches = gradient.match(hexPattern);

  if (matches && matches.length > 0) {
    return normalizeHexColor(matches[0]);
  }

  return null;
}

/** Calculates relative luminance per WCAG 2.0. */
function getLuminance(hex: string): number {
  const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const b = Number.parseInt(hex.slice(4, 6), 16) / 255;

  const sR = r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4;
  const sG = g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4;
  const sB = b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4;

  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

/** Normalizes hex color or gradient to 6-digit hex. */
function normalizeAnyColor(color: string | undefined | null): string | null {
  if (!color || typeof color !== 'string') {
    return null;
  }

  let normalizedHex = normalizeHexColor(color);

  if (!normalizedHex && color.toLowerCase().includes('gradient')) {
    normalizedHex = extractFirstHexFromGradient(color);
  }

  return normalizedHex;
}

/** Calculates contrast ratio between two colors (1 to 21). */
function getContrastRatio(color1: string | undefined | null, color2: string | undefined | null): number | null {
  const hex1 = normalizeAnyColor(color1);
  const hex2 = normalizeAnyColor(color2);

  if (!hex1 || !hex2) {
    return null;
  }

  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

// Lower than WCAG AA (4.5:1) - just prevents fully hidden text
const MINIMUM_CONTRAST_RATIO = 2;

/** Returns true if colors have enough contrast to be readable. */
export function hasMinimumContrast(
  textColor: string | undefined | null,
  backgroundColor: string | undefined | null,
): boolean {
  const ratio = getContrastRatio(textColor, backgroundColor);

  if (ratio === null) {
    return false;
  }

  return ratio >= MINIMUM_CONTRAST_RATIO;
}

/** Returns true if the color is light (luminance > 0.5). */
export function isLightColor(color: string | undefined | null): boolean {
  const hex = normalizeAnyColor(color);
  if (!hex) return false;
  return getLuminance(hex) > 0.5;
}

/** Returns black or white based on background luminance. */
export function getContrastingColor(backgroundColor: string | undefined | null): string {
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#000000';
  }

  let normalizedHex = normalizeHexColor(backgroundColor);

  if (!normalizedHex && backgroundColor.toLowerCase().includes('gradient')) {
    normalizedHex = extractFirstHexFromGradient(backgroundColor);
  }

  if (!normalizedHex) {
    return '#000000';
  }

  const luminance = getLuminance(normalizedHex);
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
